import { mediaUrl } from '../../lib/media-url.js';
import { isStripeCheckout } from '../../lib/checkout-mode.js';
import { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  ShoppingCart, 
  Check, 
  ShieldCheck, 
  Phone, 
  Mail, 
  FileText, 
  Package, 
  Send,
  Download,
  Sparkles,
  Bot,
  Award,
  BookOpen,
  FileCheck,
  HelpCircle,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table.jsx';
import { useTenant } from '../../context/TenantContext.jsx';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { useCart } from '../../store/cart.jsx';
import { EditableProductText } from '../../components/editing/EditableProductText.jsx';
import { SanitizedHtml } from '../../lib/sanitizer.jsx';
import { getProduct, getProductContext, getProductDocuments } from '../../lib/api.js';
import { formatCurrency, formatInquiryHref } from '../../lib/utils.js';
import { displayUnitCents, priceDisplayMode, vatNoticeFor, vatRateOf } from '../../lib/price.js';

const DOC_TYPE_META = {
  datasheet: { label: 'Datenblatt', variant: 'default' },
  manual: { label: 'Montageanleitung', variant: 'secondary' },
  certificate: { label: 'Zertifikat / VdS', variant: 'accent' },
  wiring_diagram: { label: 'Schaltplan & Klemmen', variant: 'outline' },
  catalog: { label: 'Fachkatalog', variant: 'secondary' },
};

export const SPEC_LABEL_MAP = {
  // Direkte ASL-Katalog-Keys (D1 & CSVs)
  'abmessungen': 'Abmessungen',
  'abmessungen d x h': 'Abmessungen (Ø × H)',
  'abmessungen b x h x t': 'Abmessungen (B × H × T)',
  'abmessungen bxhxt': 'Abmessungen (B × H × T)',
  'antenne für folgende technologien': 'Funkstandards / Technologien',
  'anzahl din-schiene': 'DIN-Schienen',
  'auflösung/bildrate': 'Auflösung & Bildrate',
  'ausgangsspannungen': 'Ausgangsspannung',
  'ausgangsspannung': 'Ausgangsspannung',
  'ausgangsstrom': 'Ausgangsstrom',
  'ausgänge': 'Ausgänge',
  'akkukapazität': 'Akkukapazität',
  'akustische signalisierung': 'Akustische Signalisierung',
  'laute akustische signalisierung': 'Akustische Signalisierung',
  'optische signalisierung': 'Optische Signalisierung',
  'benachrichtigung': 'Benachrichtigung',
  'biegeradius': 'Biegeradius',
  'bis zu 17 karten erweiterbar': 'Modulerweiterung',
  'durchmesser der installationsleitung': 'Leitungsdurchmesser',
  'en 50131 zertifizierung': 'EN 50131 Zertifizierung',
  'eingänge': 'Signaleingänge',
  'erfassung erfassungsbereich': 'Erfassungsbereich',
  'erfassungsbereich': 'Erfassungsbereich',
  'erfassungszonen': 'Erfassungszonen',
  'farbe': 'Farbe',
  'fernsteuerung': 'Fernsteuerung',
  'feuerwiderstandklasse': 'Feuerwiderstandsklasse',
  'fixobjektive': 'Objektivausführung',
  'frequenzband': 'Frequenzband',
  'frequenzbereich': 'Frequenzbereich',
  'kabellänge': 'Kabellänge',
  'kabelquerschnitt': 'Kabelquerschnitt',
  'kapazität': 'Akkukapazität',
  'leitungslänge': 'Leitungslänge',
  'länge': 'Länge',
  'material': 'Material',
  'montageplatte': 'Montageplatte',
  'notstromversorgung': 'Notstromversorgung',
  'platz für': 'Platzangebot (Akku)',
  'platz für akku': 'Akku-Steckplatz',
  'relais': 'Relaisausgänge',
  'sicherung': 'Absicherung',
  'spannung': 'Betriebsspannung',
  'steuereingänge': 'Steuereingänge',
  'steuerspannung': 'Steuerspannung',
  'umgebungstemperatur': 'Betriebstemperatur',
  'vds-nr.': 'VdS-Anerkennung',
  'verpackungseinheit': 'Verpackungseinheit',
  'verstärkung': 'Antennengewinn',
  'visuell': 'Sichtfeld / Optik',
  'zeitbereich': 'Zeitbereich',
  'übertragung': 'Übertragungsweg',

  // Standard-Keys (Englisch / CamelCase / Snake_Case)
  'voltage': 'Betriebsspannung',
  'operating_voltage': 'Betriebsspannung',
  'supply_voltage': 'Versorgungsspannung',
  'power_supply': 'Spannungsversorgung',
  'current': 'Stromaufnahme',
  'power': 'Leistungsaufnahme',
  'power_consumption': 'Leistungsaufnahme',
  'battery': 'Batterie / Akku',
  'battery_type': 'Batterietyp',
  'range': 'Reichweite',
  'detection_range': 'Erfassungsbereich',
  'resolution': 'Auflösung',
  'channels': 'Kanäle',
  'channel_count': 'Kanalanzahl',
  'frequency': 'Frequenz',
  'wireless_frequency': 'Funkfrequenz',
  'dimensions': 'Abmessungen',
  'weight': 'Gewicht',
  'operating_temperature': 'Betriebstemperatur',
  'temperature_range': 'Temperaturbereich',
  'humidity': 'Luftfeuchtigkeit',
  'protection_class': 'Schutzart',
  'ip_rating': 'Schutzart (IP)',
  'tamper': 'Sabotageschutz',
  'tamper_switch': 'Sabotagekontakt',
  'certification': 'Zertifizierung',
  'certifications': 'Zertifizierungen',
  'standards': 'Normen & Standards',
  'warranty': 'Herstellergarantie',
  'mounting': 'Montageart',
  'mounting_type': 'Montageart',
  'installation': 'Installation',
  'inputs': 'Eingänge',
  'outputs': 'Ausgänge',
  'relay_outputs': 'Relaisausgänge',
  'interface': 'Schnittstelle',
  'interfaces': 'Schnittstellen',
  'ports': 'Anschlüsse',
  'housing': 'Gehäuse',
  'enclosure': 'Gehäuse',
  'color': 'Farbe',
  'colour': 'Farbe',
  'material': 'Material',
  'brand': 'Hersteller',
  'manufacturer': 'Hersteller',
  'model': 'Modell',
  'lens': 'Objektiv',
  'focal_length': 'Brennweite',
  'field_of_view': 'Sichtfeld / FOV',
  'fov': 'Sichtfeld (FOV)',
  'angle_of_view': 'Blickwinkel',
  'audio': 'Audio',
  'microphone': 'Mikrofon',
  'speaker': 'Lautsprecher',
  'night_vision': 'Nachtsicht',
  'ir_range': 'IR-Reichweite',
  'storage': 'Speicherkapazität',
  'hdd_slots': 'Festplatten-Einschübe',
  'network': 'Netzwerk',
  'ethernet': 'LAN / Ethernet',
  'wifi': 'WLAN',
  'wlan': 'WLAN',
  'bluetooth': 'Bluetooth',
  'protocols': 'Protokolle',
  'compression': 'Videokompression',
  'video_compression': 'Videokompression',
  'frame_rate': 'Bildrate',
  'fps': 'Bildrate',
  'wdr': 'WDR-Dynamik',
  'sensor': 'Bildsensor',
  'image_sensor': 'Bildsensor',
};

export const VALUE_HEURISTICS = [
  // 1. Zertifizierung & Normen
  [/(?:\bvds\b|vds[- ]?(?:klasse|anerkannt|zertifiziert|g\s*\d+|konform|[abc]\b)|en[- ]?50131|en[- ]?54|en[- ]?12101|en[- ]?14604|en[- ]?50130|en[- ]?50136|en[- ]?\d{4,5}|din[- ]?en|grade[- ]?[1-4]|grad[- ]?[1-4]|zertifizier|ce[- ]?konform|dsgvo|bdsg|normkonform|prüfzeichen|anerkennung)/i, 'Zertifizierung & Normen'],

  // 2. Garantie & Service
  [/(?:garantie|gewährleistung|\d+\s*jahre?\s+garantie)/i, 'Herstellergarantie'],

  // 3. Sabotage & Schutzüberwachung
  [/(?:sabotage|sabotageschutz|sabotagekontakt|abrisskontakt|deckelkontakt|fremdfeldüberwacht|öffnungs[- ]?und\s+abriss|sabotageüberwachung|sabotagegeschützt)/i, 'Sabotageschutz'],

  // 4. Stromversorgung & Notstrom
  [/(?:netzteil|puffer(?:netzteil)?|versorgungsspannung|notstrom|akku|batterie|batteriebetrieb|akkubetrieb|mah\b|ah\b|li[- ]?ion|cr123a|spannungsausgang|ausgangsspannung|stromversorgung|speisung|stromausfall|tiefentladeschutz|kurzschlussschutz|überlastschutz|überspannungsschutz)/i, 'Stromversorgung & Akku'],

  // 5. Elektrische Kennwerte & Last
  [/(?:dauerbetrieb|spitzenlast|ruhestrom|stromaufnahme|schaltspannung|schaltstrom|last\s+schaltbar|\b\d+[,\.]?\d*\s*a\b|\b\d+\s*ma\b|12\s*v|24\s*v|230\s*v|48\s*v|v\s*dc|v\s*ac|vdc|vac|poe\+?|high[- ]?poe|spannung|sicherung|schmelzsicherung|oc[- ]?ausgang|klemmen|schraubklemmen)/i, 'Elektrische Kennwerte'],

  // 6. Optik, Brennweite, Sensor
  [/(?:motorzoom|brennweite|objektiv|fixobjektiv|weitwinkel|sichtfeld|blende|cmos|ccd|bildsensor|1\/1,8|1\/2,7|1\/2,8|1\/3|tag\/nacht|tag-\/nacht|ir[- ]?korrigiert|kuppel|objektive|lichtempfindlichkeit|blendenwert|linsen|wechsellinsen)/i, 'Optik & Bildsensor'],

  // 7. Videoauflösung & Videoformate
  [/(?:auflösung|megapixel|\bmp\b|\d+\s*x\s*\d+\s*px|4k|ultra\s*hd|full\s*hd|fps\b|bildrate|videokompression|h\.264|h\.265|mjpeg|aufzeichnung|aufnahmerate|nvr|dvr|videomanagement|cvi|tvi|ahd|fbas|h264|h265|videobild|videostrom|streaming)/i, 'Video & Bildübertragung'],

  // 8. Dynamikbereich & WDR
  [/(?:wdr|true\s*wdr|120\s*db|130\s*db|140\s*db|dynamikbereich|gegenlicht|blc|hlc|dnr|3d[- ]?dnr|entzerrung)/i, 'Dynamik & Bildoptimierung'],

  // 9. Beleuchtung & Anzeige
  [/(?:ir[- ]?strahler|infrarot|nachtsicht|weißlicht|led[- ]?anzeige|leds|beleuchtung|optische\s+anzeige|optische\s+signalisierung|status[- ]?led|statusanzeige|anzeige|nachtlicht|notbeleuchtung)/i, 'Beleuchtung & Anzeige'],

  // 10. KI, Videoanalyse & Erkennung
  [/(?:ki[- ]?gestützt|videoanalyse|analyse|gesichtserkennung|gesichter|kennzeichen|kennzeichenerkennung|smd|ivs|lpr|line[- ]?crossing|intrusion|smartsensor|autotracking|motion[- ]?detection|archivsuche|ereignis|ereignisse|bewegungserkennung|erkennung|blacklist|whitelist|black-?\s*&\s*whitelist|suchfunktion|suche)/i, 'KI & Videoanalyse'],

  // 11. Erfassung & Meldetechnik
  [/(?:vorhangmelder|bewegungsmelder|präsenzmelder|rauchmelder|brandmelder|wärmemelder|magnetkontakt|riegelschaltkontakt|klinkenwächter|glasbruch|erschütterung|falschalarmsicherheit|erfassungsbereich|reichweite|erfassungskorridor|detektion|überwachungsbereich|\b\d+\s*m\s+reichweite|zonen|melder|unterkriechschutz|abdecküberwachung|erfassung|überwachung)/i, 'Erfassung & Meldetechnik'],

  // 12. Audio, Sprache & Signalisation
  [/(?:akustisch|summer|sirene|lautsprecher|audio|mikrofon|mik\b|sprachausgabe|schalldruck|db\(a\)|audiokanal|2-wege[- ]?audio|reinsprechen|ton|töne|gong|audioeingang|audioausgang)/i, 'Audio & Signalisation'],

  // 13. Schutzart & Gehäuse
  [/(?:ip6[5-8]|ip5[4-5]|ik10|ik0[8-9]|nema|wetterfest|wetterbeständig|wettergeschützt|vandalschutz|vandalismus|gehäuse|edelstahl|walzstahl|alu|polycarbonat|abs[- ]?gehäuse|aluguss|schutzart|schutzklasse|robust|material|walzblech)/i, 'Gehäuse & Schutzart'],

  // 14. Schnittstellen, Funk, Netzwerk
  [/(?:rs[- ]?485|ethernet|lan\b|wlan|wifi|gsm|lte|funk|funksender|funksirene|funknetz|frequenz|antenne|ip[- ]?netzwerk|protokoll|modbus|be\s*wave|abax|contact[- ]?id|telim|vds2465|bus[- ]?melder|datenbus|verschlüsselt|verschlüsselung|aes|bluetooth|zigbee|z-wave|dect|lora|nb-iot|cat\s*5|cat\s*6|cat\s*7|netzwerk|schnittstelle|schnittstellen|ip\b)/i, 'Schnittstellen & Funk'],

  // 15. Ein- & Ausgänge / Relais
  [/(?:relais|relaisplatine|relaisstatus|wechslerrelais|meldelinie|meldelinien|meldergruppe|eingänge|ausgänge|alarmein|alarmaus|schaltkontakt|schaltleistung|2eol|3eol|no\s*\/\s*nc|draht|2-draht|steuereingänge|signaleingänge|zeitrelais|\bschaltung\b|ausgangserweiterung)/i, 'Ein- & Ausgänge'],

  // 16. Kabel & Anschlüsse
  [/(?:anschlusskabel|kabel\b|kabelführung|kabellänge|leitung|leitungslänge|kabelquerschnitt|leiterquerschnitt|doppelt\s+geschirmt|stecker|buchse|ungeschirmt|ring|metermarkierungen|biegeradius|erdungsset|sma[- ]?stecker|bnc|hdmi|vga|usb)/i, 'Kabel & Anschlüsse'],

  // 17. Montage & Einsatzbereich
  [/(?:din[- ]?schiene|hutschiene|hutschienenmontage|wandmontage|deckenmontage|mastmontage|aufputz|unterputz|einbau|aufbaumontage|19\"|he\b|rack|schaltschrank|abmessung|abmessungen|montageadapter|wandhalter|montageplatte|abdeckung|befestigung|montage|fachboden|schrank|neigungswinkel|horizontalwinkel|winkel|befestigungsmaterial|montagematerial|installationsmaterial|innenbereich|außenbereich|innen\s*und\s*außen|innen-\s*und\s*außenbereich|montageort|einsatzbereich)/i, 'Montage & Einsatzbereich'],

  // 18. Software, Fernzugriff & Steuerung
  [/(?:software|lizenz|app\b|cms\b|webserver|browser|cloud|konfiguration|fernkonfiguration|fernzugriff|parametrierung|programmierung|pc[- ]?programmierung|fernwartung|integration|fernsteuerung|sms|push|e-mail|fernbedienung|remote|client)/i, 'Software & Fernzugriff'],

  // 19. Umwelt- & Betriebsbedingungen
  [/(?:temperaturbereich|umgebungstemperatur|betriebstemperatur|feuchtigkeit|kondensation|wasserwaage|thermostat|lüfter|klimatisierung)/i, 'Betriebsbedingungen'],

  // 20. Maße, Gewicht & Verpackung
  [/(?:gewicht|\bkg\b|\b\d+\s*g\b|höhe|breite|tiefe|länge|\b\d+\s*(?:mm|cm|m)\b|\b\d+\s*meter\b|verpackungseinheit|stück|set\b)/i, 'Maße & Lieferumfang'],

  // 21. Farb- & Designvarianten
  [/(?:farbe|farben|design|weiß|schwarz|anthrazit|silber|wechselblende|wechselblenden|dekorativ|optik)/i, 'Design & Farbvariante'],

  // 22. Systemkompatibilität & Erweiterung
  [/(?:kompatibel|geeignet\s+für|zusammenarbeit|einsatz\s+mit|erweiterung|system|zentralen|integra|perfecta|satel|grundig|luna|dahua|hikvision|stand-alone|standalone|fremdsysteme|erweiterbar|kanal|kanäle|kanälen)/i, 'Systemkompatibilität'],

  // 23. Bedienung & Sicherheit
  [/(?:touchscreen|lcd|tastatur|handsender|bedienung|schlüssel|rückstellung|schutzklappe|zugriffsschutz|authentifizierung)/i, 'Bedienung & Sicherheit'],
];

/**
 * Roh-Spec-Keys (feature_1, attr_2, voltage, …) in sprechende deutsche Labels überführen.
 * @param {string} key - Roh-Schlüssel
 * @param {any} [value=''] - Optionaler Merkmalswert zur semantischen Heuristik
 * @returns {string} Sprechendes deutsches Label
 */
export function specLabel(key, value = '') {
  if (!key) return '';
  const cleanKey = String(key).trim().toLowerCase();

  // 1. Direkter Lookup im Label-Dictionary
  if (SPEC_LABEL_MAP[cleanKey]) {
    return SPEC_LABEL_MAP[cleanKey];
  }

  // 2. Roh-Feature-Key (feature_1, spec_2, attr_3, etc.)
  const m = /^(?:feature|merkmal|attr|spec)[_-]?(\d+)$/i.exec(String(key));
  if (m) {
    if (value) {
      const valStr = String(value);
      for (const [pattern, label] of VALUE_HEURISTICS) {
        if (pattern.test(valStr)) {
          return label;
        }
      }
    }
    return `Merkmal ${m[1]}`;
  }

  // 3. Fallback: Lesbare Aufbereitung für unbekannte sprechende Keys
  const cleaned = String(key)
    .replace(/[_-]+/g, ' ')
    .trim();
  if (cleaned.length > 40) {
    return cleaned.slice(0, 38) + '…';
  }
  return cleaned.replace(/^./, (c) => c.toUpperCase());
}

function formatDocSize(bytes) {
  if (!bytes || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** K2: Normen- & Fach-Assistent erst anzeigen, wenn die Funktion tatsächlich arbeitet. */
const ASSISTANT_TAB_ENABLED = false;

export function ProductDetailView({
  slug,
  idOrSlug,
  onNavigate,
  onAddToCart,
  onOpenCopilot,
}) {
  const effectiveId = slug || idOrSlug;
  const { tenant } = useTenant();
  const { canEdit, editMode } = useEditMode();
  const { isInCart, addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [ragContext, setRagContext] = useState(null);
  const [ragLoading, setRagLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [justAdded, setJustAdded] = useState(false);
  const [productDocs, setProductDocs] = useState([]);

  // a-053: Dateien-Tab (nur sichtbar, wenn Einträge existieren)
  useEffect(() => {
    let isMounted = true;
    // Beim SKU-Wechsel alte Dateien sofort verwerfen (keine Anzeige fremder Dokumente)
    setProductDocs([]);
    const sku = product?.sku;
    if (!sku) return () => { isMounted = false; };
    getProductDocuments(sku, tenant?.id)
      .then((data) => { if (isMounted && data?.ok) setProductDocs(data.documents || []); })
      .catch(() => { if (isMounted) setProductDocs([]); });
    return () => { isMounted = false; };
  }, [product?.sku, tenant?.id]);

  // Load basic product details
  useEffect(() => {
    let isMounted = true;
    async function loadProduct() {
      if (!effectiveId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getProduct(effectiveId, tenant?.id);
        if (isMounted) {
          const item = res.product || res.item || res;
          if (item && (item.id || item.sku)) {
            setProduct(item);
          } else {
            throw new Error('Produkt nicht gefunden');
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Produkt konnte nicht geladen werden');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadProduct();
    return () => { isMounted = false; };
  }, [effectiveId, tenant?.id]);

  // Load contextual RAG metadata (linked PDFs, norms, AI suggestions)
  useEffect(() => {
    let isMounted = true;
    async function loadRagContext() {
      const skuToQuery = product?.sku || product?.id;
      if (!skuToQuery) return;
      setRagLoading(true);
      try {
        const data = await getProductContext(skuToQuery, tenant?.id);
        if (isMounted && data && data.ok) {
          setRagContext(data);
        }
      } catch {
        if (isMounted) {
          // Graceful zero-document fallback
          setRagContext({
            ok: true,
            sku: skuToQuery,
            product_name: product?.name || '',
            documents: [],
            applicable_norms: [],
            suggested_queries: [
              `Technische Spezifikation für »${product?.name || 'Artikel'}« abfragen`,
              'Montagehinweise und Zertifikate'
            ],
          });
        }
      } finally {
        if (isMounted) setRagLoading(false);
      }
    }

    if (product) {
      loadRagContext();
    }
    return () => { isMounted = false; };
  }, [product, tenant?.id]);

  const handleTriggerCopilot = (promptText) => {
    if (onOpenCopilot) {
      onOpenCopilot(promptText);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('asl:open-copilot', {
        detail: { query: promptText, prompt: promptText }
      }));
    }
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-8 sm:px-6 lg:px-8 space-y-8">
        <Skeleton className="h-6 w-32" />
        <div className="grid gap-12 lg:grid-cols-2">
          <Skeleton className="aspect-square w-full rounded-2xl" />
          <div className="space-y-6">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-12 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-16 text-center space-y-4">
        <h2 className="font-display text-2xl font-bold text-[var(--foreground)]">Produkt nicht gefunden</h2>
        <p className="text-sm text-[var(--secondary)]">
          Der angeforderte Artikel existiert nicht im Sortiment oder ist derzeit nicht aktiv.
        </p>
        <Button variant="outline" onClick={() => onNavigate('/catalog')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück zum Katalog
        </Button>
      </div>
    );
  }

  const sku = product.sku || product.id || '';
  const alreadyInCart = isInCart(sku);

  // Normalize image gallery
  let images = [];
  if (Array.isArray(product.images) && product.images.length > 0) {
    images = product.images.map(img => typeof img === 'string' ? img : (img.url || ''));
  } else if (product.images_json) {
    try {
      const parsed = JSON.parse(product.images_json);
      if (Array.isArray(parsed)) {
        images = parsed.map(img => typeof img === 'string' ? img : (img.url || ''));
      }
    } catch {}
  }
  if (images.length === 0 && (product.primaryImageUrl || product.image_url)) {
    images = [product.primaryImageUrl || product.image_url];
  }

  // Normalize specs
  let specs = {};
  if (product.specs && typeof product.specs === 'object') {
    specs = product.specs;
  } else if (product.specs_json) {
    try {
      specs = JSON.parse(product.specs_json);
    } catch {}
  }

  const unitPriceCents = product.price?.cents !== undefined 
    ? Number(product.price.cents) 
    : (product.base_price !== undefined ? Number(product.base_price) : (Number(product.unitPriceCents) || 0));

  const isBuyable = unitPriceCents > 0;
  // a-055b: Anzeige wird aus price.cents berechnet (Modus: Kauf brutto, Anfrage/OCI netto).
  // Der Serverwert product.price.formatted wird bewusst ignoriert, siehe src/lib/price.js.
  const priceMode = priceDisplayMode(tenant);
  const vatRate = vatRateOf(tenant);
  const formattedPrice = formatCurrency(displayUnitCents(unitPriceCents, priceMode, vatRate));

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart(product, quantity);
    } else {
      addItem(product, quantity);
    }
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2000);
  };

  // Unpriced articles ("Auf Anfrage") must still enter the inquiry cart as a
  // line item (SKU + quantity) — previously this CTA only navigated and left
  // the cart empty.
  const handleInquiryAdd = () => {
    addItem(product, quantity);
    onNavigate('/cart');
  };

  const mailtoHref = formatInquiryHref(product, tenant);
  const documents = ragContext?.documents || [];
  const applicableNorms = ragContext?.applicable_norms || [];
  const suggestedQueries = ragContext?.suggested_queries || [
    `Technische Spezifikation für »${product.name || 'Artikel'}« abfragen`,
    'Montagehinweise und Zertifikate'
  ];

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 sm:px-6 lg:px-8 space-y-12">
      
      {/* Breadcrumb / Back button */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onNavigate('/catalog')}
          className="gap-2 text-[var(--secondary)] hover:text-[var(--foreground)] cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          Katalog
        </Button>
        <span className="text-xs text-[var(--secondary)]">/</span>
        <span className="text-xs font-semibold text-[var(--secondary)] truncate max-w-md">
          {product.name}
        </span>
      </div>

      {/* Main Grid: Gallery + Product Info */}
      <div className="grid gap-12 lg:grid-cols-2">
        
        {/* Left: Gallery */}
        <div className="space-y-4">
          <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-3xl border border-[var(--border)] bg-white p-8 shadow-sm">
            {images.length > 0 && images[selectedImageIndex] ? (
              <img
                src={mediaUrl(images[selectedImageIndex])}
                alt={product.name}
                className="h-full w-full object-contain"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-[var(--secondary)]/40 gap-2">
                <Package className="h-16 w-16" />
                <span className="text-xs font-bold uppercase tracking-wider">Keine Produktabbildung</span>
              </div>
            )}

            {(product.isFeatured || product.is_featured) && (
              <span className="absolute top-4 left-4 rounded-md bg-[var(--primary)] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[var(--on-primary)] shadow-sm">
                Empfehlung
              </span>
            )}
          </div>

          {/* Gallery Thumbnails */}
          {images.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-white p-2 transition-all cursor-pointer ${
                    selectedImageIndex === idx ? 'border-[var(--primary)] ring-2 ring-[var(--ring)]' : 'border-[var(--border)] opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={mediaUrl(img)} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Info & Actions */}
        <div className="space-y-6">
          
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold text-xs uppercase tracking-wider text-[var(--primary)] bg-[var(--primary)]/10 px-2.5 py-0.5 rounded-full">
                {product.manufacturer || 'Fachgroßhandel'}
              </span>
              <span className="text-xs font-mono text-[var(--secondary)]">
                Art.-Nr. {product.sku}
              </span>
              {applicableNorms.length > 0 && (
                <Badge variant="accent" className="gap-1 font-bold text-xs lowercase-none">
                  <ShieldCheck className="h-3 w-3" />
                  {applicableNorms[0]}
                </Badge>
              )}
            </div>

            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[var(--foreground)] leading-tight">
                {product.name}
            </h1>
          </div>

          {/* Price Box */}
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-container-low)] p-6 space-y-4">
            <div className="flex items-baseline justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[var(--secondary)]">
                  {isBuyable ? 'Preis' : 'Preisstatus'}
                </p>
                <p className="font-display text-3xl font-extrabold text-[var(--foreground)]">
                  {isBuyable ? formattedPrice : 'Auf Anfrage'}
                </p>
              </div>
              <span className="text-xs text-[var(--secondary)] font-medium">
                {vatNoticeFor(priceMode, vatRate)}
              </span>
            </div>

            {/* Quantity + Add to Cart */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              {isBuyable ? (
                <>
                  <div className="flex items-center rounded-[var(--radius)] border border-[var(--border)] bg-white px-3 py-1">
                    <button
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="px-2 py-1 text-base font-bold text-[var(--secondary)] hover:text-[var(--foreground)] cursor-pointer"
                      aria-label="Menge verringern"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      className="w-12 text-center text-sm font-bold border-none outline-none"
                    />
                    <button
                      onClick={() => setQuantity(q => q + 1)}
                      className="px-2 py-1 text-base font-bold text-[var(--secondary)] hover:text-[var(--foreground)] cursor-pointer"
                      aria-label="Menge erhöhen"
                    >
                      +
                    </button>
                  </div>

                  <Button
                    size="lg"
                    className="flex-1 gap-2 font-bold shadow-sm cursor-pointer"
                    onClick={handleAddToCart}
                  >
                    {justAdded ? (
                      <>
                        <Check className="h-5 w-5 text-emerald-400" />
                        Hinzugefügt!
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-5 w-5" />
                        {alreadyInCart ? (isStripeCheckout(tenant) ? 'Menge im Warenkorb erhöhen' : 'Menge im Anfragekorb erhöhen') : (isStripeCheckout(tenant) ? 'In den Warenkorb hinzufügen' : 'In den Anfragekorb')}
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  size="lg"
                  className="w-full gap-2 font-bold shadow-sm cursor-pointer"
                  onClick={handleInquiryAdd}
                >
                  <Send className="h-5 w-5" />
                  Projektangebot anfragen
                </Button>
              )}
            </div>

            {/* Direct Mail Inquiry Link */}
            {mailtoHref ? (
              <div className="pt-2 text-center sm:text-left">
                <a
                  href={mailtoHref}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--primary)] hover:underline"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Direkte E-Mail-Anfrage zu diesem Artikel stellen
                </a>
              </div>
            ) : (
              <div className="pt-2 text-center sm:text-left text-xs text-[var(--secondary)] italic">
                <span>E-Mail-Anfrage direkt nicht verfügbar (keine Mandanten-E-Mail hinterlegt)</span>
              </div>
            )}
          </div>

          {/* Description */}
          {(product.description || product.shortDescription || product.short_description || (canEdit && editMode)) && (
            <div className="space-y-2">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
                Produktbeschreibung
              </h3>
              <div className="prose prose-sm text-sm leading-relaxed text-[var(--secondary)] whitespace-pre-line">
                <EditableProductText
                  productId={product.id || product.sku}
                  field="description"
                  value={product.description || product.shortDescription || product.short_description || ''}
                  onUpdate={(res) => {
                    const effectiveDesc = res?.description?.effective || res?.description || product.description;
                    setProduct((prev) => ({ ...prev, description: effectiveDesc }));
                  }}
                >
                  <SanitizedHtml
                    content={product.description || product.shortDescription || product.short_description || ''}
                    keyPrefix="product-desc"
                  />
                </EditableProductText>
              </div>
            </div>
          )}

          {/* Partner Advice Trust Box */}
          <Card className="border-[var(--border)] bg-white shadow-sm">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-wider text-[var(--primary)]">
                <ShieldCheck className="h-4 w-4" />
                <span>Fachberatung & Projektpreise</span>
              </div>
              <p className="text-xs text-[var(--secondary)] leading-relaxed">
                Sie planen ein größeres Objekt oder benötigen Unterstützung bei der Systemauslegung? Die Experten von {tenant?.name || 'Ihrem Fachpartner'} beraten Sie gerne persönlich.
              </p>
              {tenant?.phone && (
                <div className="flex items-center gap-2 text-xs font-bold text-[var(--foreground)] pt-1">
                  <Phone className="h-3.5 w-3.5 text-[var(--primary)]" />
                  <span>Telefon: {tenant.phone}</span>
                </div>
              )}
            </CardContent>
          </Card>

        </div>

      </div>

      {/* Tabs Section: Technische Daten & Normen-Assistent */}
      <section className="space-y-6 border-t border-[var(--border)] pt-8">
        <Tabs defaultValue="specs" className="w-full space-y-6">
          <TabsList className="w-full flex-wrap justify-start border-b border-[var(--border)] bg-transparent p-0 rounded-none h-auto gap-x-4 gap-y-1">
            <TabsTrigger
              value="specs"
              className="rounded-none whitespace-normal border-b-2 border-transparent data-[state=active]:border-[var(--primary)] data-[state=active]:bg-transparent data-[state=active]:text-[var(--primary)] data-[state=active]:shadow-none py-3 px-4 text-sm sm:text-base font-bold cursor-pointer gap-2"
            >
              <FileText className="h-4 w-4" />
              Technische Daten
            </TabsTrigger>
            {productDocs.length > 0 && (
              <TabsTrigger
                value="files"
                className="rounded-none whitespace-normal border-b-2 border-transparent data-[state=active]:border-[var(--primary)] data-[state=active]:bg-transparent data-[state=active]:text-[var(--primary)] data-[state=active]:shadow-none py-3 px-4 text-sm sm:text-base font-bold cursor-pointer gap-2"
              >
                <Download className="h-4 w-4" />
                Dateien
                <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0 font-bold">{productDocs.length}</Badge>
              </TabsTrigger>
            )}
            {ASSISTANT_TAB_ENABLED && (
              <TabsTrigger
                value="assistant"
                className="rounded-none whitespace-normal border-b-2 border-transparent data-[state=active]:border-[var(--primary)] data-[state=active]:bg-transparent data-[state=active]:text-[var(--primary)] data-[state=active]:shadow-none py-3 px-4 text-sm sm:text-base font-bold cursor-pointer gap-2"
              >
                <Bot className="h-4 w-4 text-[var(--primary)]" />
                Normen- & Fach-Assistent
                {documents.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0 font-bold">
                    {documents.length}
                  </Badge>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Tab 1: Technische Daten */}
          <TabsContent value="specs" className="mt-0 space-y-4">
            {Object.keys(specs).length > 0 ? (
              <div className="rounded-2xl border border-[var(--border)] bg-white overflow-hidden shadow-xs">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/3">Merkmal</TableHead>
                      <TableHead>Spezifikation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(specs).map(([key, val]) => (
                      <TableRow key={key}>
                        <TableCell className="font-semibold text-[var(--foreground)]">{specLabel(key, val)}</TableCell>
                        <TableCell className="text-[var(--secondary)]">{String(val)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-[var(--border)] p-8 text-center bg-white space-y-2">
                <FileText className="h-8 w-8 text-[var(--secondary)]/40 mx-auto" />
                <p className="text-sm font-semibold text-[var(--foreground)]">Keine tabellarischen Spezifikationen hinterlegt</p>
                <p className="text-xs text-[var(--secondary)]">Nutzen Sie den Normen- & Fach-Assistenten für detaillierte Auskünfte und Handbücher.</p>
              </div>
            )}
          </TabsContent>

          {/* Tab: Dateien (a-053) */}
          {productDocs.length > 0 && (
            <TabsContent value="files" className="mt-0 space-y-3">
              <ul className="space-y-3">
                {productDocs.map((d) => (
                  <li key={d.linkId} className="flex items-center gap-3 rounded-2xl border bg-white p-4 shadow-xs">
                    <FileText className="h-5 w-5 shrink-0 text-[var(--primary)]" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[var(--foreground)]">{d.filename}</p>
                      <Badge variant={d.isPrimary ? 'default' : 'secondary'} className="mt-1 text-xs">{d.label}</Badge>
                    </div>
                    {d.url ? (
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${d.filename} herunterladen`}
                        className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[var(--primary)] hover:bg-[var(--muted)]"
                      >
                        <Download className="h-5 w-5" aria-hidden="true" />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </TabsContent>
          )}

          {/* Tab 2: Normen- & Fach-Assistent */}
          <TabsContent value="assistant" className="mt-0 space-y-8">
            
            {/* ALWIS Assistant Intro Banner */}
            <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-container-low)] p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[var(--primary)] text-[var(--on-primary)] shadow-sm">
                  <Bot className="h-6 w-6" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-bold text-[var(--foreground)]">
                      ALWIS Fach- & Normen-Assistent
                    </h3>
                    <Badge variant="accent" className="text-xs">Assistent</Badge>
                  </div>
                  <p className="text-xs text-[var(--secondary)] leading-relaxed max-w-2xl">
                    Kontextuelle Sicherheitsnormen (VdS / DIN EN), Hersteller-Dokumente und Berechnungen direkt für diesen Artikel abrufen.
                  </p>
                </div>
              </div>

              <Button
                variant="default"
                size="sm"
                className="gap-2 shrink-0 font-bold cursor-pointer"
                onClick={() => handleTriggerCopilot(`Details und Normen für »${product.name || ''}« (SKU: ${product.sku}) prüfen`)}
              >
                <Sparkles className="h-4 w-4" />
                Fach-Chat öffnen
              </Button>
            </div>

            {/* Normen & Zertifikate */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-[var(--primary)]" />
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
                  Normen & Zertifikate
                </h4>
              </div>

              {applicableNorms.length > 0 ? (
                <div className="flex flex-wrap gap-2 pt-1">
                  {applicableNorms.map((norm, idx) => (
                    <div
                      key={idx}
                      className="inline-flex items-center gap-2 rounded-xl border border-[var(--border)] bg-white px-3.5 py-2 shadow-2xs hover:border-[var(--primary)] transition-colors"
                    >
                      <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span className="text-xs font-bold text-[var(--foreground)]">{norm}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-[var(--border)] bg-white p-4 text-xs text-[var(--secondary)] flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[var(--secondary)]/60 shrink-0" />
                  <span>Hersteller-Standardkonformität (Keine gesonderten VdS/EN-Zertifikate hinterlegt).</span>
                </div>
              )}
            </div>

            {/* Verknüpfte Dokumente & Datenblätter */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-[var(--primary)]" />
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
                  Verknüpfte Fachdokumente & Handbücher ({documents.length})
                </h4>
              </div>

              {documents.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 pt-1">
                  {documents.map((doc) => {
                    const meta = DOC_TYPE_META[doc.document_type] || { label: doc.document_type, variant: 'secondary' };
                    return (
                      <div
                        key={doc.doc_id}
                        className="flex flex-col justify-between rounded-2xl border border-[var(--border)] bg-white p-4 shadow-2xs hover:border-[var(--primary)] hover:shadow-xs transition-all space-y-3"
                      >
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <Badge variant={meta.variant} className="text-xs">
                              {meta.label}
                            </Badge>
                            {doc.is_primary && (
                              <Badge variant="accent" className="text-xs">
                                Primär
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs font-bold text-[var(--foreground)] leading-snug line-clamp-2" title={doc.filename}>
                            {doc.filename}
                          </p>
                        </div>

                        <div className="flex items-center justify-between border-t border-[var(--border)] pt-3 text-xs text-[var(--secondary)]">
                          <span>
                            {doc.page_count} {doc.page_count === 1 ? 'Seite' : 'Seiten'}
                            {doc.file_size_bytes ? ` • ${formatDocSize(doc.file_size_bytes)}` : ''}
                          </span>

                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-bold text-[var(--primary)] hover:underline cursor-pointer"
                          >
                            <Download className="h-3.5 w-3.5" />
                            PDF öffnen
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-[var(--border)] bg-white p-4 text-xs text-[var(--secondary)] flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-[var(--secondary)]/60 shrink-0" />
                  <span>Für diesen Artikel sind aktuell keine PDF-Dokumente im zentralen Master-Katalog verknüpft.</span>
                </div>
              )}
            </div>

            {/* 1-Click ALWIS Copilot Prompts */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[var(--primary)]" />
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
                  1-Click ALWIS Fachberatung & KI-Berechnungen
                </h4>
              </div>

              <div className="grid gap-2.5 sm:grid-cols-2 pt-1">
                {suggestedQueries.map((queryText, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleTriggerCopilot(queryText)}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-white p-3.5 text-left text-xs font-semibold text-[var(--foreground)] shadow-2xs hover:border-[var(--primary)] hover:bg-[var(--surface-container-low)] hover:text-[var(--primary)] transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)] group-hover:bg-[var(--primary)] group-hover:text-[var(--on-primary)] transition-colors">
                        <Sparkles className="h-3.5 w-3.5" />
                      </div>
                      <span className="truncate">{queryText}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[var(--secondary)] group-hover:text-[var(--primary)] shrink-0 transition-transform group-hover:translate-x-0.5" />
                  </button>
                ))}
              </div>
            </div>

          </TabsContent>
        </Tabs>
      </section>

    </div>
  );
}

export { ProductDetailView as ProductView };
